import { useHistory } from "react-router-dom";

import Box from "@/components/Box";
import { ProductFiltersDesktop } from "@/components/ProductFiltersSidebar";
import { theme } from "@/core/theme";
import EmptySearch from "@/tpo/EmptySearch";
import Group from "@/tpo/Group";
import InfiniteScroll, { InfiniteScrollContext } from "@/tpo/InfiniteScroll";
import Stack from "@/tpo/Stack";

import LearningHubListSidebar from "./LearningHubListSidebar";
import LearningHubListToolbar from "./LearningHubListToolbar";
import {
  dashboardMainLayoutPb,
  dashboardMainLayoutPt,
  dashboardMainLayoutPx,
  dashboardMainStackMaxWidth,
  dashboardPageMaxWidth,
  dashboardSidebarContentFlexDirection,
  dashboardShopListControlsToContentGap,
  dashboardShopListSubtitleControlsGap,
  dashboardSidebarContentGap
} from "./dashboardLayout";
import useList from "./useList";

export function ListTemplateInfiniteScrollObjectList({
  Container,
  containerProps,
  loading,
  objects,
  objectsPlural,
  hasMore,
  fetchMore,
  endCursor,
  Card,
  cardProps
}) {
  if (!loading && !objects?.length) {
    return <EmptySearch message={`No ${objectsPlural} found`} {...containerProps} />;
  }

  return (
    <Container {...containerProps}>
      <InfiniteScroll
        hasMore={hasMore}
        next={() =>
          fetchMore({
            variables: {
              after: endCursor,
              first: 10
            }
          })
        }
        loading={loading}
      >
        <InfiniteScrollContext.Consumer>
          {({ setBottomElement }) => {
            function renderCard(obj, ref) {
              return <Card {...cardProps} {...obj} key={obj.id} ref={ref} />;
            }

            return (
              <>
                {objects?.slice(0, -1)?.map(obj => renderCard(obj))}
                {objects?.slice(-1)?.map(obj => renderCard(obj, setBottomElement))}
              </>
            );
          }}
        </InfiniteScrollContext.Consumer>
      </InfiniteScroll>
    </Container>
  );
}

export default function ListTemplate({
  icon,
  pageTitle,
  pageDescription,
  query,
  connectionName,
  objectsPlural,
  Card,
  cardProps,
  Container,
  containerProps,
  defaultSort,
  sortOptions,
  queryVariables,
  filtersTitle,
  showFilters = true
}) {
  const {
    allFilters,
    validatedFilters,
    toggleFilter,
    resetFilters,
    controls,
    location,
    fetchMore,
    loading,
    objects,
    hasMore,
    endCursor,
    search
  } = useList({
    defaultSort,
    query,
    connectionName,
    sortOptions,
    queryVariables,
    showFilters
  });

  const history = useHistory();

  const handleSearchChange = value => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("search", value);
    history.push({
      path: location.path,
      search: searchParams.toString()
    });
  };

  const filterDesktop = showFilters ? (
    <ProductFiltersDesktop
      allFilters={allFilters}
      validatedFilters={validatedFilters}
      toggleFilter={toggleFilter}
      onReset={resetFilters}
    />
  ) : null;

  return (
    <Group
      flexDirection={dashboardSidebarContentFlexDirection}
      justifyContent="center"
      pt={dashboardMainLayoutPt}
      pb={dashboardMainLayoutPb}
      px={dashboardMainLayoutPx}
      maxWidth={dashboardPageMaxWidth}
      mx="auto"
      gap={dashboardSidebarContentGap}
    >
      <LearningHubListSidebar filterDesktop={filterDesktop} />
      <Stack
        gap={[...dashboardShopListSubtitleControlsGap.slice(0, 2), 80]}
        flexGrow={1}
        width="100%"
        minWidth={0}
        maxWidth={dashboardMainStackMaxWidth}
      >
        <Stack gap={20}>
          <Group gap={20} alignItems="center">
            {icon}
            <Box fontFamily="gilroyBold" fontSize={[36, 36, 68]}>
              {pageTitle}
            </Box>
          </Group>
          {pageDescription && (
            <Box fontFamily="gilroyMedium" fontSize={[16, 16, 18]}>
              {pageDescription}
            </Box>
          )}
        </Stack>
        <Stack gap={0} width="100%" minWidth={0}>
          <Stack gap={20} minWidth={0} width="100%">
            <LearningHubListToolbar
              allFilters={allFilters}
              validatedFilters={validatedFilters}
              toggleFilter={toggleFilter}
              resetFilters={resetFilters}
              filtersTitle={filtersTitle}
              search={search}
              onSearchChange={handleSearchChange}
              sortControls={controls}
              showFilters={showFilters}
            />
          </Stack>
          <Stack mt={dashboardShopListControlsToContentGap} gap={0} minWidth={0} width="100%">
            <ListTemplateInfiniteScrollObjectList
              Container={Container}
              containerProps={containerProps}
              loading={loading}
              objects={objects}
              objectsPlural={objectsPlural}
              hasMore={hasMore}
              fetchMore={fetchMore}
              endCursor={endCursor}
              Card={Card}
              cardProps={cardProps}
            />
          </Stack>
        </Stack>
      </Stack>
    </Group>
  );
}

ListTemplate.defaultProps = {
  Container: Stack,
  containerProps: {
    gap: 16,
    pb: theme.spacing.section.pb
  }
};

ListTemplate.InfiniteScrollObjectList = ListTemplateInfiniteScrollObjectList;
